import { GridRenderCellParams } from "@mui/x-data-grid";
import { Chip, Stack, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { RenderDeleteButton } from "../../components/RenderDeleteButtonTable";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { routes } from "../../../utils/constant/navigationRoutes";
import { CuisineRow } from "../../../utils/preference/cuisinePayload";
import { token } from "../../components/FormKit/styled";

interface CuisineTableColumnsProps {
  handleOnDelete: (value: CuisineRow) => void;
}

const MAX_CATEGORY_CHIPS = 3;

export const useCuisineTableColumns = ({
  handleOnDelete,
}: CuisineTableColumnsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dateColumns: AllowedTableColumn[] = ["updatedAt", "createdAt"];

  const handleOnClickEdit = (value: CuisineRow) => {
    navigate(routes.preferenceCuisine.editById(value?.id || ""));
  };

  const columns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      renderCell: (props: GridRenderCellParams) => (
        <PermissionComponentWrapper
          requiredPermission={["edit"]}
          requiredPermissionGroup={["tag"]}
        >
          <RenderEditButton onClick={handleOnClickEdit} {...props} />
        </PermissionComponentWrapper>
      ),
      sortable: false,
    },
    {
      field: "index",
      width: 90,
      headerName: `${t(messages.preference_cuisine_index())}`,
      type: "number",
    },
    {
      field: "name",
      width: 200,
      headerName: `${t(messages.preference_cuisine_name())}`,
    },
    {
      field: "nameChinese",
      width: 160,
      headerName: `${t(messages.preference_cuisine_name_chinese())}`,
      valueGetter: (props: GridRenderCellParams<CuisineRow>) =>
        props.row.nameChinese || "—",
    },
    {
      field: "nameFrench",
      width: 180,
      headerName: `${t(messages.preference_cuisine_name_french())}`,
      valueGetter: (props: GridRenderCellParams<CuisineRow>) =>
        props.row.nameFrench || "—",
    },
    {
      field: "slug",
      width: 190,
      headerName: `${t(messages.preference_cuisine_slug())}`,
      renderCell: (props: GridRenderCellParams<CuisineRow>) => (
        <Tooltip title={t(messages.preference_slug_tooltip())} arrow>
          <span
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
              color: token.color.textMuted,
            }}
          >
            {props.row.slug}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "categories",
      width: 280,
      headerName: `${t(messages.preference_cuisine_categories())}`,
      sortable: false,
      renderCell: (props: GridRenderCellParams<CuisineRow>) => {
        const categories = props.row.categories ?? [];
        if (!categories.length) return <span>—</span>;
        const shown = categories.slice(0, MAX_CATEGORY_CHIPS);
        const restCount = categories.length - shown.length;
        return (
          <Tooltip
            title={categories.map((category) => category.name).join(", ")}
            arrow
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              {shown.map((category) => (
                <Chip
                  key={category.id}
                  size="small"
                  label={category.name}
                  sx={{ height: 22, fontSize: 12 }}
                />
              ))}
              {restCount > 0 && (
                <Chip
                  size="small"
                  label={`+${restCount}`}
                  sx={{ height: 22, fontSize: 12 }}
                />
              )}
            </Stack>
          </Tooltip>
        );
      },
    },
    ...dateColumns.map((value) => ({
      width: 200,
      field: value,
      headerName: `${t(messages[value]())}`,
      type: "dateTime",
      valueGetter: (val: GridRenderCellParams) =>
        val?.row?.[value] ? new Date(val.row[value]) : undefined,
    })),
    {
      field: "id",
      width: 300,
      headerName: `${t(messages["id"]())}`,
    },
    {
      field: "delete",
      width: 90,
      headerName: `${t(messages.Delete())}`,
      renderCell: (props: GridRenderCellParams) => (
        <PermissionComponentWrapper
          requiredPermission={["delete"]}
          requiredPermissionGroup={["tag"]}
        >
          <RenderDeleteButton
            onClick={() => handleOnDelete(props.row as CuisineRow)}
            {...props}
          />
        </PermissionComponentWrapper>
      ),
      sortable: false,
    },
  ];

  return columns;
};
